import {IconButton, Stack, Tooltip, Typography} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ReactComponent as SendIcon} from "../../../assets/icons/arrow-narrow-up.svg";
import {ReactComponent as SquareIcon} from "../../../assets/icons/square.svg";
import {CHAT_CONSTANTS} from '../../../config/chat.constants';
import {useDragAndDrop} from '../../../hooks/useDragAndDrop';
import {useFileValidation} from '../../../utils/fileValidation';
import {useConversation} from '../../../contexts/ConversationContext';
import {AttachmentPreviewItem, DISPLAY_ITEM_TYPE, useSelectedFiles} from '../../../contexts/SelectedFilesContext';
import {
    ActionsWrapper,
    Container,
    InputActions,
    InputWrapper,
    SendButtonWrapper,
    StyledForm,
    StyledTextField
} from './ChatInput.styled';
import FilePicker from '../FilePicker/FilePicker';
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {parseError} from "../../../errors/http.service";
import FileAttachmentList from "../FileAttachmentList";

interface ChatInputProps {
    onSendMessage: (text: string, files: File[] | null, searchInternet: boolean, documentIds?: number[]) => void;
    onStopGeneration?: () => void;
    isGenerating?: boolean;
    disabledInput?: boolean;
    conversationId?: string | null;
}

const ChatInput = ({
    onSendMessage,
    onStopGeneration,
    isGenerating = false,
    disabledInput = false,
    conversationId,
}: ChatInputProps) => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();

    const [text, setText] = useState('');
    const [searchInternet] = useState(false);
    const [isMultiLine, setIsMultiLine] = useState(false);

    const [isSending, setIsSending] = useState(false);
    const isSendingRef = useRef(false);

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const {validateFile} = useFileValidation();
    const {state, autoScrollFinished} = useConversation();
    const {
        attachedFiles,
        attachedSourceCollections,
        setAttachedFiles,
        handleRemoveLocalFiles,
        handleRemoveSourceDocGroup,
        handleRemoveAllSourceCollections,
        handleRemoveAllFiles,
        hasFiles,
        displayItems
    } = useSelectedFiles();

    const isAnyFileInConversation = !!conversationId && (
        state.messages.some(m => m.documents && m.documents.length > 0) || (state.documents && state.documents.length > 0)
    );

    const handleFilesAccepted = (files: File[]) => {
        if (attachedFiles.length + files.length > CHAT_CONSTANTS.MAX_DISK_ATTACHMENTS) {
            showSnackbar(t('chatInput.fileLimitReachedWarning', {maxFiles: CHAT_CONSTANTS.MAX_DISK_ATTACHMENTS}), 'warning');
            return;
        }

        setAttachedFiles([...attachedFiles, ...files]);
    };

    const {
        isDraggingOver,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop
    } = useDragAndDrop({
        onFileAccepted: handleFilesAccepted,
        validateFile,
        disabled: isGenerating || disabledInput,
        currentFiles: attachedFiles,
        maxFiles: CHAT_CONSTANTS.MAX_DISK_ATTACHMENTS
    });

    useEffect(() => {
        isSendingRef.current = false;
        setIsSending(false);
        handleRemoveAllFiles();
        inputRef?.current?.focus();
    }, []);

    useEffect(() => {
        setText('');
        if (!isSendingRef.current) {
            isSendingRef.current = false;
            setIsSending(false);
        }
        handleRemoveAllFiles();
        inputRef?.current?.focus();
    }, [conversationId]);

    useEffect(() => {
        if (isGenerating) {
            isSendingRef.current = false;
            setIsSending(false);
        }
    }, [isGenerating]);

    useEffect(() => {
        inputRef.current?.focus();
    }, [hasFiles]);

    useEffect(() => {
        const checkMultiLine = () => {
            if (text.length === 0) {
                if (isMultiLine) setIsMultiLine(false);
                return;
            }
            if (!isMultiLine && text.length > 50) {
                setIsMultiLine(true);
            } else if (isMultiLine && text.length < 30) {
                setIsMultiLine(false);
            }
        };
        checkMultiLine();
    }, [text, isMultiLine]);

    const handleSend = async () => {
        if ((text.trim() || hasFiles) && !isGenerating && !disabledInput && !isSendingRef.current) {
            isSendingRef.current = true;
            setIsSending(true);

            const currentText = text;
            const currentFiles = attachedFiles;

            try {
                onSendMessage(currentText, currentFiles.length > 0 ? currentFiles : null, searchInternet);

                // TODO: here probably error with two extensions
                // if (currentFiles.length > 0) {
                //     onSendMessage(currentText, currentFiles, searchInternet);
                // }
                //
                // if (currentSourceDocs.length > 0) {
                //     const docs = await Promise.all(
                //         currentSourceDocs.map(doc =>
                //             getDocumentFile(doc.id, `${doc.name}.${doc.extension}`)
                //         )
                //     );
                //     onSendMessage(currentText, docs.length > 0 ? docs : null, searchInternet);
                // } else {
                //     onSendMessage(currentText, currentFiles.length > 0 ? currentFiles : null, searchInternet);
                // }
            } catch (error) {
                console.error('Failed to send message:', error);
                const message = parseError(error);
                if (message)
                    showSnackbar(message, 'error');

                isSendingRef.current = false;
                setIsSending(false);
            } finally {
                setText('');
                if (hasFiles)
                    handleRemoveAllFiles();
            }
        }
    };

    const handleRemoveAttachedFile = (item: AttachmentPreviewItem) => {
        if (item.type === DISPLAY_ITEM_TYPE.SOURCE_COLLECTION || item.type === DISPLAY_ITEM_TYPE.SOURCE_COLLECTIONS || attachedSourceCollections.length > 0) {
            handleRemoveAllSourceCollections();
            return;
        }

        if (item.type === DISPLAY_ITEM_TYPE.SOURCE_DOCUMENTS && item.sourceId != null) {
            handleRemoveSourceDocGroup(item.sourceId);
            return;
        }

        handleRemoveLocalFiles();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();

            if (!isSendingRef.current) {
                void handleSend();
            }
        }
    };

    useEffect(() => {
        if (autoScrollFinished) {
            inputRef?.current?.focus();
        }
    }, [autoScrollFinished]);

    return (
        <Container>
            <StyledForm
                ref={formRef}
                $hasFile={hasFiles}
                $isMultiLine={isMultiLine}
                $isDraggingOver={isDraggingOver}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <FileAttachmentList items={displayItems}
                                    onRemove={handleRemoveAttachedFile}
                />
                <InputWrapper>
                    {!isMultiLine && (
                        <InputActions $attachedFile={hasFiles} $isMultiLine={isMultiLine}>
                            <FilePicker
                                disabled={disabledInput}
                                isGenerating={isGenerating || isSending}
                                isAnyFileInConversation={isAnyFileInConversation}
                            />
                            {/* AI-727 [To be possibly uncommented in the future] */}
                            {/*<Tooltip title={t('chatInput.searchInternet')}>*/}
                            {/*    <span>*/}
                            {/*        <GlobeButton*/}
                            {/*            onClick={() => setSearchInternet(v => !v)}*/}
                            {/*            $isInternetSelected={searchInternet}*/}
                            {/*            disabled={isGenerating || disabled}*/}
                            {/*        >*/}
                            {/*            <GlobeIcon/>*/}
                            {/*        </GlobeButton>*/}
                            {/*    </span>*/}
                            {/*</Tooltip>*/}

                            {/* AI-754 [To be possibly uncommented in the future] */}
                            {/*<Tooltip title={t('chatInput.voiceRecording')}>*/}
                            {/*    <span>*/}
                            {/*        <MicrophoneButton*/}
                            {/*            onClick={handleVoiceRecognition}*/}
                            {/*            $isRecording={isRecording}*/}
                            {/*            disabled={isGenerating || disabled}*/}
                            {/*        >*/}
                            {/*            <MicIcon/>*/}
                            {/*        </MicrophoneButton>*/}
                            {/*    </span>*/}
                            {/*</Tooltip>*/}
                        </InputActions>
                    )}
                    <StyledTextField
                        value={text}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => setText(e.target.value)}
                        fullWidth
                        placeholder={t('chatInput.placeholder')}
                        variant="outlined"
                        multiline
                        maxRows={4}
                        $hasFile={hasFiles}
                        inputRef={inputRef}
                        inputProps={{maxLength: CHAT_CONSTANTS.MAX_MESSAGE_LENGTH}}
                        disabled={isGenerating || disabledInput || isSending}
                        style={{pointerEvents: (isGenerating || disabledInput || isSending) ? 'none' : 'auto'}}
                    />
                    {!isMultiLine && (
                        <SendButtonWrapper>
                            {isGenerating ? (
                                <Tooltip title={t('chatInput.stopGeneration')}>
                                    <IconButton
                                        onClick={onStopGeneration}
                                        sx={{
                                            '& svg': {width: '20px', height: '20px'}
                                        }}
                                    >
                                        <SquareIcon/>
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Tooltip title={t('chatInput.send')}>
                                    <span style={{ display: 'inline-flex', pointerEvents: 'auto' }}>
                                        <IconButton
                                            disabled={(!text.trim() && !hasFiles) || disabledInput || isSending}
                                            onClick={handleSend}
                                            style={{pointerEvents: 'auto'}}
                                        >
                                            <SendIcon/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </SendButtonWrapper>
                    )}
                </InputWrapper>

                {isMultiLine && (
                    <ActionsWrapper>
                        <InputActions $attachedFile={hasFiles} $isMultiLine={isMultiLine}>
                            <FilePicker
                                isGenerating={isGenerating}
                                isAnyFileInConversation={isAnyFileInConversation}
                                disabled={disabledInput}
                            />
                            {/* AI-727 [To be possibly uncommented in the future] */}
                            {/*<Tooltip title={t('chatInput.searchInternet')}>*/}
                            {/*    <span>*/}
                            {/*        <GlobeButton*/}
                            {/*            onClick={() => setSearchInternet(v => !v)}*/}
                            {/*            $isInternetSelected={searchInternet}*/}
                            {/*            disabled={isGenerating || disabled}*/}
                            {/*        >*/}
                            {/*            <GlobeIcon/>*/}
                            {/*        </GlobeButton>*/}
                            {/*    </span>*/}
                            {/*</Tooltip>*/}

                            {/* AI-754 [To be possibly uncommented in the future] */}
                            {/*<Tooltip title={t('chatInput.voiceRecording')}>*/}
                            {/*    <span>*/}
                            {/*        <MicrophoneButton*/}
                            {/*            onClick={handleVoiceRecognition}*/}
                            {/*            $isRecording={isRecording}*/}
                            {/*            disabled={isGenerating || disabled}*/}
                            {/*        >*/}
                            {/*            <MicIcon/>*/}
                            {/*        </MicrophoneButton>*/}
                            {/*    </span>*/}
                            {/*</Tooltip>*/}
                        </InputActions>
                        <SendButtonWrapper style={{ pointerEvents: 'auto' }}>
                            {isGenerating ? (
                                <Tooltip title={t('chatInput.stopGeneration')}>
                                    <IconButton
                                        onClick={onStopGeneration}
                                        sx={{
                                            '& svg': {width: '20px', height: '20px'}
                                        }}
                                    >
                                        <SquareIcon/>
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Tooltip title={t('chatInput.send')}>
                                    <span style={{ display: 'inline-flex', pointerEvents: 'auto' }}>
                                        <IconButton
                                            disabled={(!text.trim() && !hasFiles) || disabledInput || isSending}
                                            onClick={handleSend}
                                            style={{ pointerEvents: 'auto' }}
                                        >
                                            <SendIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </SendButtonWrapper>
                    </ActionsWrapper>
                )}
            </StyledForm>

            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.hint" sx={{ mt: 1, mb: 1 }}>
                    {t('chatInput.description')}
                </Typography>

                <Typography variant="caption" color="text.hint" sx={{ ml: 4, mt: 1, mb: 1 }}>
                    {text.length}/{CHAT_CONSTANTS.MAX_MESSAGE_LENGTH}
                </Typography>
            </Stack>

        </Container>
    );
};

export default ChatInput;
